// import { Box, Button, FormControl, Typography } from '@mui/material';
// import { useField, useFormikContext } from 'formik';
// import React from 'react';
// import { resizeImage } from './resizeImage';
// import { uploadImage } from '../../HelperFunctions/s3Service';

// interface ImageUploadFieldProps {
//     name: string;
//     label: string;
//     multiple?: boolean;
//     maxFiles?: number;
//     recommendedText?: string;
// }

// const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
//     name,
//     label,
//     multiple = false,
//     maxFiles = 1,
//     recommendedText,
// }) => {
//     const [field, meta] = useField(name);
//     const form = useFormikContext<any>();

//     const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
//         const files = Array.from(e.target.files || []);
//         form.setFieldTouched(name, true);

//         if (files.length === 0) {
//             form.setFieldError(name, `Please upload ${label.toLowerCase()}`);
//             return;
//         }

//         const existing = multiple ? field.value || [] : [];
//         if (multiple && existing.length + files.length > maxFiles) {
//             form.setFieldError(name, `Maximum ${maxFiles} ${label.toLowerCase()} images allowed`);
//             return;
//         }

//         try {
//             const resizedFiles = await Promise.all(files.map(file => resizeImage(file, label)));
//             const uploaded = await Promise.all(resizedFiles.map(file => uploadImage(file)));
//             const urls = uploaded.map(res => res.url);

//             const newValue = multiple ? [...existing, ...urls] : urls[0];
//             form.setFieldValue(name, newValue);
//             form.setFieldError(name, undefined);
//         } catch (err) {
//             console.error(err);
//             form.setFieldError(
//                 name,
//                 err instanceof Error ? err.message : `Error uploading ${label.toLowerCase()}`
//             );

//         }
//     };

//     const handleClear = () => {
//         form.setFieldValue(name, multiple ? [] : null);
//         form.setFieldError(name, undefined);
//     };

//     const fileInputId = `${name}-input`;
//     const hasValue = multiple ? Array.isArray(field.value) && field.value.length > 0 : !!field.value;

//     return (
//         <FormControl error={Boolean(meta.touched && meta.error)} fullWidth>
//             <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
//                 <Box sx={{ flex: 1 }}>
//                     <input
//                         type="file"
//                         accept="image/*"
//                         multiple={multiple}
//                         onChange={handleChange}
//                         style={{ display: 'none' }}
//                         id={fileInputId}
//                     />

//                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                         <label htmlFor={fileInputId}>
//                             <Button
//                                 variant="outlined"
//                                 component="span"
//                                 sx={{
//                                     textTransform: 'uppercase',
//                                     height: '35px',
//                                     bgcolor: 'background.paper',
//                                     color: 'black',
//                                     borderColor: 'black',
//                                     '&:hover': {
//                                         borderColor: 'black',
//                                         backgroundColor: 'rgba(0, 0, 0, 0.04)',
//                                     },
//                                 }}
//                             >
//                                 Choose File
//                             </Button>
//                         </label>

//                         <Typography variant="body2" color="text.secondary">
//                             {hasValue
//                                 ? multiple
//                                     ? `${field.value.length} image${field.value.length > 1 ? 's' : ''} uploaded`
//                                     : 'File selected'
//                                 : 'No file chosen'}
//                         </Typography>

//                         {hasValue && (
//                             <Button
//                                 variant="contained"
//                                 color="error"
//                                 onClick={handleClear}
//                                 sx={{ height: '30px', textTransform: 'uppercase' }}
//                             >
//                                 Clear
//                             </Button>
//                         )}
//                     </Box>

//                     {/* Preview Section */}
//                     {hasValue && (
//                         <Box mt={2} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
//                             {(multiple ? field.value : [field.value]).map((url: string, idx: number) => (
//                                 <Box key={idx} sx={{ position: 'relative' }}>
//                                     <img
//                                         src={url}
//                                         alt={`${label} ${idx + 1}`}
//                                         style={{
//                                             width: multiple ? 200 : 150,
//                                             height: multiple ? 100 : 150,
//                                             objectFit: 'cover',
//                                             borderRadius: 4,
//                                             border: '1px solid #ddd',
//                                         }}
//                                     />
//                                 </Box>
//                             ))}
//                         </Box>
//                     )}

//                     {/* Text under image preview */}
//                     {recommendedText && (
//                         <Typography
//                             variant="caption"
//                             color="text.secondary"
//                             sx={{ display: 'block', mt: 1 }}
//                         >
//                             {recommendedText}
//                         </Typography>
//                     )}
//                 </Box>
//             </Box>
//         </FormControl>
//     );
// };

// export default ImageUploadField;


import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import React, { useState, useRef } from 'react';
import { resizeImage } from './resizeImage';
import { uploadImageWithPresignedUrl } from '../../HelperFunctions/s3Service';
import CloseIcon from '@mui/icons-material/Close';
import { useContext } from 'react';
import { HimmaContext } from '../../../context/HimmaContext';

interface ImageUploadFieldProps {
    name: string;
    label: string;
    multiple?: boolean;
    maxFiles?: number;
    recommendedText?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
    name,
    label,
    multiple = false,
    maxFiles = 1,
    recommendedText,
}) => {
    const [field, meta] = useField(name);
    const form = useFormikContext<any>();
    const apiClient = useContext(HimmaContext);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        form.setFieldTouched(name, true);

        if (files.length === 0) {
            form.setFieldError(name, `Please upload ${label.toLowerCase()}`);
            return;
        }

        const existing = multiple ? field.value || [] : [];
        if (multiple && existing.length + files.length > maxFiles) {
            form.setFieldError(name, `Maximum ${maxFiles} ${label.toLowerCase()} images allowed`);
            return;
        }

        try {
            if (!apiClient) {
                throw new Error('API client not available');
            }
            const resizedFiles = await Promise.all(files.map(file => resizeImage(file, label)));
            const uploaded = await Promise.all(resizedFiles.map(file => uploadImageWithPresignedUrl(file, apiClient)));
            const urls = uploaded.map(res => res.url);

            const newValue = multiple ? [...existing, ...urls] : urls[0];
            form.setFieldValue(name, newValue);
            form.setFieldError(name, undefined);
        } catch (err) {
            console.error(err);

            const errorMessage =
                typeof err === 'string'
                    ? err
                    : err instanceof Error
                        ? err.message
                        : `Error uploading ${label.toLowerCase()}`;

            form.setFieldError(name, errorMessage);
        }

    };

    const handleClear = () => {
        form.setFieldValue(name, multiple ? [] : null);
        form.setFieldError(name, undefined);
        // Reset the file input value so the same file can be selected again
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleDeleteImage = (indexToDelete: number) => {
        if (multiple) {
            const existingUrls = Array.isArray(field.value) ? field.value : [];
            const updatedUrls = existingUrls.filter((_: any, index: number) => index !== indexToDelete);
            form.setFieldValue(name, updatedUrls);
            if (updatedUrls.length === 0) {
                form.setFieldError(name, undefined);
            }
        } else {
            form.setFieldValue(name, null);
            form.setFieldError(name, undefined);
        }
        // Reset the file input value so the same file can be selected again
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const fileInputId = `${name}-input`;
    const hasValue = multiple ? Array.isArray(field.value) && field.value.length > 0 : !!field.value;
    const uploadedImages = React.useMemo(() => {
        if (!field.value) return [];

        if (multiple) {
            // Normalize: extract url from objects if needed
            return (field.value as any[]).map(img =>
                typeof img === 'string' ? img : img?.url || ''
            ).filter(Boolean);
        }

        // Single image case
        const single = field.value;
        if (typeof single === 'string') return [single];
        if (typeof single === 'object' && single?.url) return [single.url];
        return [];
    }, [field.value, multiple]);
    return (
        <FormControl error={Boolean(meta.touched && meta.error)} fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple={multiple}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                        id={fileInputId}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <label htmlFor={fileInputId}>
                            <Button
                                variant="outlined"
                                component="span"
                                sx={{
                                    textTransform: 'uppercase',
                                    height: '35px',
                                    bgcolor: 'background.paper',
                                    color: 'black',
                                    borderColor: 'black',
                                    '&:hover': {
                                        borderColor: 'black',
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                Choose File
                            </Button>
                        </label>

                        <Typography variant="body2" color="text.secondary">
                            {hasValue
                                ? multiple
                                    ? `${field.value.length} image${field.value.length > 1 ? 's' : ''} uploaded`
                                    : 'File selected'
                                : 'No file chosen'}
                        </Typography>

                        {hasValue && (
                            <>
                                {multiple && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleClear}
                                        sx={{ height: '30px', textTransform: 'uppercase' }}
                                    >
                                        Clear All
                                    </Button>
                                )}
                                {!multiple && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleClear}
                                        sx={{ height: '30px', textTransform: 'uppercase' }}
                                    >
                                        Clear
                                    </Button>
                                )}

                                <Button
                                    variant="outlined"
                                    onClick={() => setPreviewOpen(true)}
                                    sx={{ height: '30px', textTransform: 'uppercase' }}
                                >
                                    Preview
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* Image Thumbnails */}
                    {hasValue && uploadedImages.length > 0 && (
                        <Box mt={2} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {uploadedImages.map((url: string, idx: number) => (
                                <Box key={idx} sx={{ position: 'relative' }}>
                                    <img
                                        src={url}
                                        alt={`${label} ${idx + 1}`}
                                        style={{
                                            width: multiple ? 200 : 150,
                                            height: multiple ? 100 : 150,
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                            border: '1px solid #ddd',
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => handleDeleteImage(idx)}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            width: 24,
                                            height: 24,
                                            '&:hover': {
                                                bgcolor: 'error.dark',
                                            },
                                        }}
                                        size="small"
                                    >
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Recommended Text */}
                    {recommendedText && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            {recommendedText}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Preview {label}
                    <IconButton
                        aria-label="close"
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
                        <Tab label="Mobile Image" />
                        <Tab label="Web Image" />
                    </Tabs>

                    {uploadedImages.length > 0 ? (
                        <>
                            {uploadedImages.map((url: string, idx: number) => (
                                <Box key={idx} sx={{ display: tabIndex === 0 ? 'block' : 'none' }}>
                                    {/* Mobile Preview */}
                                    <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
                                        <img
                                            src={url}
                                            alt={`Mobile Preview ${idx + 1}`}
                                            style={{
                                                width: '100%',
                                                maxWidth: '300px',
                                                height: 'auto',
                                                objectFit: 'contain',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                            }}
                                            onError={(e) => {
                                                console.error('Image load error:', url);
                                                const errorDiv = document.createElement('div');
                                                errorDiv.innerHTML = `
                                                  <div style="
                                                    display: flex;
                                                    flex-direction: column;
                                                    align-items: center;
                                                    justify-content: center;
                                                    padding: 20px;
                                                    border: 2px dashed #ccc;
                                                    border-radius: 8px;
                                                    background-color: #f9f9f9;
                                                    color: #666;
                                                    text-align: center;
                                                  ">
                                                    <div style="font-size: 48px; margin-bottom: 10px;">🖼️</div>
                                                    <div>Failed to load image</div>
                                                    <div style="font-size: 12px; margin-top: 5px; word-break: break-all;">${url}</div>
                                                  </div>
                                                `;
                                                e.currentTarget.parentNode?.replaceChild(errorDiv, e.currentTarget);
                                            }}
                                            onLoad={() => {
                                                console.log('Image loaded successfully:', url);
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            Mobile Preview {idx + 1}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}

                            {uploadedImages.map((url: string, idx: number) => (
                                <Box key={idx} sx={{ display: tabIndex === 1 ? 'block' : 'none' }}>
                                    {/* Web Preview */}
                                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                                        <img
                                            src={url}
                                            alt={`Web Preview ${idx + 1}`}
                                            style={{
                                                width: '100%',
                                                maxWidth: '600px',
                                                height: 'auto',
                                                objectFit: 'cover',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                            }}
                                            onError={(e) => {
                                                console.error('Image load error:', url);
                                                const errorDiv = document.createElement('div');
                                                errorDiv.innerHTML = `
                                                  <div style="
                                                    display: flex;
                                                    flex-direction: column;
                                                    align-items: center;
                                                    justify-content: center;
                                                    padding: 20px;
                                                    border: 2px dashed #ccc;
                                                    border-radius: 8px;
                                                    background-color: #f9f9f9;
                                                    color: #666;
                                                    text-align: center;
                                                  ">
                                                    <div style="font-size: 48px; margin-bottom: 10px;">🖼️</div>
                                                    <div>Failed to load image</div>
                                                    <div style="font-size: 12px; margin-top: 5px; word-break: break-all;">${url}</div>
                                                  </div>
                                                `;
                                                e.currentTarget.parentNode?.replaceChild(errorDiv, e.currentTarget);
                                            }}
                                            onLoad={() => {
                                                console.log('Image loaded successfully:', url);
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            Web Preview {idx + 1}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No image to preview
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </FormControl>
    );
};

export default ImageUploadField;
